import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EstensaSignService, NotifyPartialSuccessData, NotifyPartialSuccessDetailedData } from '../estensasign.service';
import {
    SeArubaSignInfo,
    SeCompedSignInfo,
    SeConfirmoSignInfo,
    SeInfoCertSignInfo,
    SeInsielARSSSignInfo,
    SeSignInfo,
    SeSignRequestData,
    SeSissCloudSignInfo,
    SeSissWayCloudSignInfo,
    SignType,
} from '../estensasign.model';
import { TranslateService } from '@ngx-translate/core';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '@se/se.ui.html5';
import { BehaviorSubject, Subscription, combineLatest, map, take } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { seSignOtpSmsRequest } from '../estensasign.actions';
import { selectSeSignOtpSms } from '../estensasign.selectors';
import { OtpSmsState, isOtpSmsInError } from '../estensasign.state';

@Component({
    selector: 'se-sign-auth-dialog',
    templateUrl: './sign-auth-dialog.component.html',
    styleUrls: ['./sign-auth-dialog.component.scss'],
})
export class SignAuthDialogComponent implements OnDestroy, OnInit {
    @ViewChild('signAuthDialogTemplate')
    signAuthDialogTemplate!: TemplateRef<any>;

    @ViewChild('pin')
    pinElement: ElementRef;

    @ViewChild('otp')
    otpElement: ElementRef;

    private signAuthDialog: MatDialogRef<any>;
    private afterCloseEventSubscription: Subscription;

    otpSmsStatus$ = this.store.pipe(select(selectSeSignOtpSms));

    private readonly signErrorMessage$ = new BehaviorSubject('');
    readonly errorMessage$ = combineLatest([this.signErrorMessage$, this.otpSmsStatus$]).pipe(
        map(([signError, otpSmsStatus]) => this.getCombinedErrorMessage(signError, otpSmsStatus)),
    );

    showPin: boolean = false;

    get sessionHelpMsg() {
        return this.translateService.instant('SignSessionHelpMsg');
    }

    get activeMessage() {
        return this.signService.leaveSessionOpen
            ? this.translateService.instant('SignSessionEnabled')
            : this.translateService.instant('SignSessionNotEnabled');
    }

    get timeoutMessage() {
        return (
            this.translateService.instant('SignSessionDuration') + ' [' + this.translateService.instant('Minutes') + ']'
        );
    }

    get pinLabel() {
        switch (this.currentSignType) {
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return 'Password';
            default:
                return 'Pin';
        }
    }

    get currentSignType(): SignType {
        return this.signService.currentSignType;
    }

    get isWaiting(): BehaviorSubject<boolean> {
        return this.signService.isWaiting;
    }

    set timeout(value: number) {
        this.signService.timeout = value;
    }

    get timeout(): number {
        return this.signService.timeout;
    }

    set leaveSessionOpen(value: boolean) {
        this.signService.leaveSessionOpen = value;
    }

    get leaveSessionOpen(): boolean {
        return this.signService.leaveSessionOpen;
    }

    get isRequestOtpSmsDisabled$() {
        return this.otpSmsStatus$.pipe(
            // Necessario perché altrimenti la proprietà cambia solo quando cambia lo stato dell'sms, mentre deve
            //   cambiare ad es. al cambio del PIN .
            take(1),
            map((status) => {
                switch (this.currentSignType) {
                    case SignType.SISSCloud:
                    case SignType.SISSWayCloud:
                        return !this.data.info.pin || status === 'loading';
                    default:
                        return true;
                }
            }),
        );
    }

    // data members

    isOnWarning = false;
    warningMessage = '';
    isOnError = false;
    data: SeSignInfo;
    oneTimePwd = ''; // Temporanea percontenere l'otp che va poi mappata sulla strutturadati

    private openSubscription: Subscription;
    private openFromErrorSubscription: Subscription;
    private notifySuccessSubscription: Subscription;
    private notifyFailureSubscription: Subscription;

    // constructor

    constructor(
        public dialog: MatDialog,
        private modalNotificationService: ModalDialogNotificationService,
        private notificationService: NotificationService,
        private signService: EstensaSignService,
        private store: Store,
        private translateService: TranslateService,
    ) {
        // Subscription open normale
        this.openSubscription = this.signService.openSignLoginWindowRequired.subscribe(
            (signData: SeSignRequestData) => {
                this.showDialog(signData);
            },
        );

        // Subscription open from error
        this.openFromErrorSubscription = this.signService.openSignLoginWindowFromErrorRequired.subscribe(
            (signData: SeSignRequestData) => {
                this.isOnError = true;
                const errorMessage = signData.initialErrorMessage
                    ? signData.initialErrorMessage
                    : this.translateService.instant(this.signService.errorMessageKey, { p0: '' });
                this.signErrorMessage$.next(errorMessage);

                this.showDialog(signData);
            },
        );

        // Subscription Success
        this.notifySuccessSubscription = this.signService.notifySignSuccess.subscribe((result) => {
            if (this.signAuthDialog) {
                this.signAuthDialog
                    .afterClosed()
                    .pipe(take(1))
                    .subscribe(() => {
                        this._notifySuccess(result);
                    });
                this.signAuthDialog.close(); // Non tutte le firme hanno la finestra aperta
            } else {
                this._notifySuccess(result);
            }
        });

        // Subscription Failure
        this.notifyFailureSubscription = this.signService.notifySignFailure.subscribe((message) => {
            this.isOnError = true;

            const genericErrorMessage = this.translateService.instant(this.signService.errorMessageKey, { p0: '' }); // Firma Fallita by default;
            this.signErrorMessage$.next(genericErrorMessage);

            switch (this.currentSignType) {
                case SignType.CompedDigitalSign:
                case SignType.InfoCert:
                case SignType.Confirmo:
                    this.data.info.pin = ''; // Resetto la password
                    setTimeout(() => {
                        if (this.pinElement?.nativeElement) this.pinElement.nativeElement.focus();
                    }, 0);

                    break;
                case SignType.InsielARSS:
                case SignType.ARUBA:
                    this.data.info.pin = ''; // Resetto la password
                    this.oneTimePwd = ''; // Resetto la otp
                    setTimeout(() => {
                        if (this.otpElement?.nativeElement) this.otpElement.nativeElement.focus();
                    }, 0);

                    this.handleARSSError(message);

                    break;
                case SignType.SISSWay:
                case SignType.SISS:
                case SignType.SISSCloud:
                case SignType.SISSWayCloud:
                    this.modalNotificationService.notify({
                        message: this.translateService.instant(message),
                        title: this.translateService.instant('Error'),
                        type: NotificationType.error,
                        buttonYes: false,
                        buttonNo: false,
                        buttonOk: true,
                        buttonCancel: false,
                    });
                    break;
            }

            // Aggiungo il dettaglio al messaggio generico nella notifica
            this.notificationService.error(message || '', genericErrorMessage);
        });
    }

    // public functions
    ngOnInit() {
        this.init();
    }

    ngOnDestroy() {
        this.afterCloseEventSubscription?.unsubscribe();
        this.openSubscription.unsubscribe();
        this.openFromErrorSubscription.unsubscribe();
        this.notifySuccessSubscription.unsubscribe();
        this.notifyFailureSubscription.unsubscribe();
    }

    sessionToggleChanged() {
        this.signService.leaveSessionOpen = !this.signService.leaveSessionOpen;
    }

    closeModal(cancelOps: boolean) {
        if (!cancelOps) {
            // Operazioni preliminari alla firma vera e propria
            this.preSignOps();

            // Richiedo firma
            this.signService.endSigningFlow(this.data);
        } else {
            // Chiudo
            this.signService.cancelSigningFlow();
            this.signAuthDialog.close(true);
        }
    }

    enableSignButton() {
        switch (this.currentSignType) {
            case SignType.ARUBA:
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return !!this.data && !!this.data.info.signerId && !!this.data.info.pin && !!this.oneTimePwd;
            case SignType.InsielARSS:
                return !!this.data && !!this.data.info.signerId && !!this.oneTimePwd;
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
                return !!this.data && !!this.data.info.signerId && !!this.data.info.pin;
        }

        return false;
    }

    isPinRequired() {
        switch (this.currentSignType) {
            case SignType.InsielARSS:
                return false;
            case SignType.ARUBA:
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return true;
        }

        return true;
    }

    isOtpRequired() {
        switch (this.currentSignType) {
            case SignType.ARUBA:
            case SignType.InsielARSS:
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return true;
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
                return false;
        }

        return false;
    }

    isTimeoutRequired() {
        switch (this.currentSignType) {
            case SignType.ARUBA:
            case SignType.InsielARSS:
                return false;
            case SignType.CompedDigitalSign:
            case SignType.InfoCert:
            case SignType.Confirmo:
                return true;
        }

        return false;
    }

    canSessionStayOpen() {
        return false;
    }

    canRequestOtpSms() {
        switch (this.currentSignType) {
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return true;
            default:
                return false;
        }
    }

    onRequestOtpSms(event: MouseEvent) {
        event.preventDefault();

        switch (this.currentSignType) {
            case SignType.SISSCloud:
                this.store.dispatch(
                    seSignOtpSmsRequest({
                        signerId: this.data.sissCloud.signerId,
                        signerCode: this.data.sissCloud.signerName,
                        password: this.data.sissCloud.pin,
                    }),
                );
                break;
            case SignType.SISSWayCloud:
                this.store.dispatch(
                    seSignOtpSmsRequest({
                        signerId: this.data.sissWayCloud.signerId,
                        signerCode: this.data.sissWayCloud.signerName,
                        password: this.data.sissWayCloud.pin,
                    }),
                );
                break;
        }
    }

    // private functions

    private showDialog(signData: SeSignRequestData) {
        this.data = new SeSignInfo();
        this.data.type = this.currentSignType;
        // Inizializzazione in base al tipo di firma
        switch (this.currentSignType) {
            case SignType.CompedDigitalSign:
                this.data.comped = new SeCompedSignInfo(signData);
                this.timeout = 60; // By default metto il timeout a 60 minuti
                break;
            case SignType.ARUBA:
                this.data.aruba = new SeArubaSignInfo(signData);
                break;
            case SignType.InfoCert:
                this.data.infoCert = new SeInfoCertSignInfo(signData);
                break;
            case SignType.Confirmo:
                this.data.confirmo = new SeConfirmoSignInfo(signData);
                break;
            case SignType.InsielARSS:
                this.data.insielARSS = new SeInsielARSSSignInfo(signData);
                break;
            case SignType.SISSCloud:
                this.data.sissCloud = new SeSissCloudSignInfo(signData);
                break;
            case SignType.SISSWayCloud:
                this.data.sissWayCloud = new SeSissWayCloudSignInfo(signData);
                break;
            default: {
                this.modalNotificationService.error(this.translateService.instant('Exception_SignNotImplementedFault'));
                return;
            }
        }

        // Apertura dialog
        this.signAuthDialog = this.dialog.open(this.signAuthDialogTemplate, {
            autoFocus: true,
            height: '35%',
            width: '25%',
            minWidth: '480px',
            minHeight: '455px',
            panelClass: 'outer-sign-dialog-wrapper',
        });

        this.afterCloseEventSubscription = this.signAuthDialog.afterClosed().subscribe(() => {
            this.signService.safeExit(); // Pulizia servizio
            this.init(); // pulizia controller
        });
    }

    private preSignOps = () => {
        switch (this.currentSignType) {
            case SignType.ARUBA:
                this.data.aruba.otp = this.oneTimePwd;
                this.oneTimePwd = '';
                break;
            case SignType.InsielARSS:
                this.data.insielARSS.otp = this.oneTimePwd;
                this.oneTimePwd = '';
                break;
            case SignType.SISSCloud:
                this.data.sissCloud.otp = this.oneTimePwd;
                this.oneTimePwd = '';
                break;
            case SignType.SISSWayCloud:
                this.data.sissWayCloud.otp = this.oneTimePwd;
                this.oneTimePwd = '';
                break;
        }
    };

    private init = () => {
        this.oneTimePwd = '';
        this.isOnError = false;
        this.signErrorMessage$.next('');
        if (!!this.data && !!this.data.info) {
            this.data.info.resetData();
        }

        if (this.afterCloseEventSubscription) {
            this.afterCloseEventSubscription.unsubscribe();
        }
    };

    private handleARSSError(message: string): void {
        // Gestione errore ARUBA e INSIEL
        let newMessage = this.translateService.instant(this.signService.errorMessageKey, { p0: '' }); // Generico
        try {
            if (message.toUpperCase().includes('KO-00')) {
                let index = message.toUpperCase().indexOf('KO-00');
                newMessage += ' (' + message.substring(index) + ')';
            }
        } finally {
            this.signErrorMessage$.next(newMessage);
        }
    }

    private _notifySuccess(result: void | NotifyPartialSuccessData | NotifyPartialSuccessDetailedData) {
        if (!result) {
            this.notificationService.success(this.translateService.instant('DigitallySigned'));
            this.modalNotificationService.notify({
                message: this.translateService.instant('SuccessOperation', { p0: '' }),
                title: this.translateService.instant('Information'),
                type: NotificationType.information,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
        } else if ('unsignedDocsDescriptions' in result) {
            this.modalNotificationService.notify({
                message: this.translateService.instant('PartiallySignedDocOnTotal', {
                    p0: result.unsignedDocsCount,
                    p1: result.docsCount,
                }),
                messageListData: result.unsignedDocsDescriptions,
                title: this.translateService.instant('Warning'),
                type: NotificationType.warning,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });

        } else {
            this.modalNotificationService.notify({
                message: this.translateService.instant('PartiallySignedDocOnTotal', {
                    p0: result.unsignedDocsCount,
                    p1: result.docsCount,
                }),
                title: this.translateService.instant('Warning'),
                type: NotificationType.warning,
                buttonYes: false,
                buttonNo: false,
                buttonOk: true,
                buttonCancel: false,
            });
        }
    }

    private getCombinedErrorMessage(signError: string, otpSmsStatus: OtpSmsState) {
        if (signError) {
            return signError;
        }
        if (isOtpSmsInError(otpSmsStatus)) {
            return this.translateService.instant(otpSmsStatus.error);
        }
        return '';
    }
}
